import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';
import { type PublicSsoConfig } from './rootEndpoints';
import { type TeamRole } from './teamEndpoints';

// Mirrored from the backend (`sso-config.model.ts`): a value added there has to be added here
// to be offered.
export const ssoProviders = ['entra', 'oidc'] as const;
export type SsoProvider = typeof ssoProviders[number];

/** The roles a sign-in may hand out. `sysadmin` is never among them. */
export const ssoAssignableRoles = ['user', 'admin'] as const;
export type SsoAssignableRole = typeof ssoAssignableRoles[number];

export const ssoTokenAuthMethods = ['client_secret_basic', 'client_secret_post'] as const;
export type SsoTokenAuthMethod = typeof ssoTokenAuthMethods[number];

export interface SsoClaims {
  subject: string;
  email: string;
  name: string;
  username: string;
}

export interface SsoTeamAssignment {
  teamId: string;
  role: TeamRole;
}

/** A value of the mapping claim, and what holding it gives: a role, a place in a team, or both. */
export interface SsoMappingRule {
  value: string;
  role?: SsoAssignableRole;
  teamId?: string;
  teamRole?: TeamRole;
}

export interface SsoAccounts {
  linkExisting: boolean;
  autoCreate: boolean;
  assignment: 'default' | 'mapping';
  defaultRole: SsoAssignableRole;
  defaultTeams: SsoTeamAssignment[];
  mappingClaim: string;
  mappings: SsoMappingRule[];
  /** Who manages an account single sign-on creates while placing by rules. */
  newAccountsManagedBy: 'sso' | 'local';
}

/** The settings as the server shows them: everything but the client secret itself. */
export interface SsoConfig {
  enabled: boolean;
  provider: SsoProvider;
  displayName: string;
  publicUrl: string;
  tenantId: string | null;
  issuer: string | null;
  clientId: string;
  /** `unreadable`: stored, but the server can no longer open it, so it has to be entered again. */
  clientSecret: 'none' | 'set' | 'unreadable';
  clientSecretSetAt: string | null;
  clientSecretExpiresAt: string | null;
  scopes: string[];
  tokenAuthMethod: SsoTokenAuthMethod;
  claims: SsoClaims;
  requireVerifiedEmail: boolean;
  accounts: SsoAccounts;
  passwordLogin: PublicSsoConfig['passwordLogin'];
  /** The address to register with the provider; `null` until the public address is set. */
  redirectUri: string | null;
  resolvedIssuer: string | null;
  /** The last trial sign-in that passed with the connection as it is stored. */
  testedAt: string | null;
}

/**
 * What `PUT /v1/sso/config` takes: any of the settings, and a part left out stays as it is
 * stored. `clientSecret` is written and never read back. `accounts` goes whole.
 */
export type SsoConfigUpdate = Partial<Pick<SsoConfig,
  'enabled' | 'provider' | 'displayName' | 'publicUrl' | 'clientId' | 'scopes' | 'tokenAuthMethod' | 'claims'
  | 'requireVerifiedEmail' | 'accounts' | 'passwordLogin'
>> & {
  tenantId?: string;
  issuer?: string;
  clientSecret?: string;
  clientSecretExpiresAt?: string | null;
};

export type SsoConnectionResult =
  | {
    ok: true;
    issuer: string;
    authorizationEndpoint?: string;
    tokenEndpoint?: string;
    userinfoEndpoint?: string;
    signingKeys: number;
    pkce: boolean;
    unsupportedScopes: string[];
  }
  | { ok: false; error: string; message: string };

/** A team an account joins, leaves, or holds another role in; `null` for "not a member". */
export interface SsoTeamChange {
  id: string;
  name: string;
  from: TeamRole | null;
  to: TeamRole | null;
}

/** What placing an account by the rules changes, or would: a row of every preview. */
export interface SsoAccountChange {
  userId: string;
  username: string;
  name: string;
  /** Whether single sign-on decides the account's role and teams from now on, no longer, still, or not now. */
  governance: 'starts' | 'ends' | 'stays' | 'none';
  role: { from: string; to: string };
  teams: SsoTeamChange[];
  /** No rule holds: it leaves every team, is signed out and refused. */
  refused: boolean;
  /** Nothing usable is known of its last sign-in: signed out, and placed at the next. */
  pending: boolean;
  /** It signs in through the provider only from now on, though it has a password. */
  passwordLoginEnds: boolean;
}

/** What a save of the settings or a handover does: the accounts something happens to, and how many others there are. */
export interface SsoChangeSummary {
  changes: SsoAccountChange[];
  unchanged: number;
}

/** What saving settings would do to the accounts. */
export interface SsoImpact extends SsoChangeSummary {
  /** Accounts tied to an identity that the new connection would no longer find. */
  unreachable: number;
}

/** What a trial sign-in found: who the provider says it was, and what would follow. */
export interface SsoTestResult {
  ok: boolean;
  error?: string;
  message?: string;
  identity?: {
    subject: string;
    email?: string;
    emailVerified: boolean;
    name?: string;
    username?: string;
    mappingClaim: string;
    mappingValues: string[];
    groupsOverage: boolean;
    /** The userinfo endpoint, where the values are, did not answer: nothing is known of them. */
    mappingUnknown?: boolean;
  };
  account?: {
    action: 'existing' | 'linked' | 'created';
    username: string;
    name: string;
    email?: string;
    role: string;
    teams: { id: string; name: string; role: TeamRole }[];
    placed: boolean;
  };
}

export const ssoApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSsoConfig: builder.query<SsoConfig, void>({
      query: () => '/v1/sso/config',
      providesTags: ['SsoConfig'],
      onQueryStarted: handleApiResult(),
    }),
    // `Config` too: the public part of these settings is what the sign-in screen reads. The
    // users and the teams as well: a save places the accounts single sign-on manages.
    updateSsoConfig: builder.mutation<void, SsoConfigUpdate>({
      query: (config) => ({ url: '/v1/sso/config', method: 'PUT', body: config }),
      invalidatesTags: ['SsoConfig', 'Config', 'User', 'Team'],
      // A refusal of fields is shown on the fields, by the card or the drawer.
      onQueryStarted: handleApiResult('Api_ServerConfig_Update_Successful', { quietFieldErrors: true }),
    }),
    // What the save of the same body would do to the accounts; nothing is written.
    previewSsoImpact: builder.mutation<SsoImpact, SsoConfigUpdate>({
      query: (config) => ({ url: '/v1/sso/config/impact', method: 'POST', body: config }),
      onQueryStarted: handleApiResult(undefined, { quietFieldErrors: true }),
    }),
    testSsoConnection: builder.mutation<SsoConnectionResult, void>({
      query: () => ({ url: '/v1/sso/config/test', method: 'POST' }),
      onQueryStarted: handleApiResult(),
    }),
    startSsoTestLogin: builder.mutation<{ url: string }, { redirectUri: string; codeChallenge: string; state: string }>({
      query: (body) => ({ url: '/v1/sso/config/test-login', method: 'POST', body }),
      onQueryStarted: handleApiResult(),
    }),
    // A trial that passed is recorded on the settings, which are read again for it.
    finishSsoTestLogin: builder.mutation<SsoTestResult, { code: string; codeVerifier: string }>({
      query: (body) => ({ url: '/v1/sso/config/test-result', method: 'POST', body }),
      invalidatesTags: ['SsoConfig'],
      onQueryStarted: handleApiResult(),
    }),
  }),
});

export const {
  useGetSsoConfigQuery,
  useUpdateSsoConfigMutation,
  usePreviewSsoImpactMutation,
  useTestSsoConnectionMutation,
  useStartSsoTestLoginMutation,
  useFinishSsoTestLoginMutation,
} = ssoApiSlice;
